import { computed, inject, Injectable } from "@angular/core";
import { CiblageArticles } from "../../utils/portee-ciblage";
import { ContexteEssaiService } from "app/shared/services/contexte-essai.service";
import { ControleApiService } from "./controle-api.service";
import { ControleState, ControleStoreService } from "./controle-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Controle } from "../../models/controle.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { firstValueFrom } from "rxjs";
import { ChecklistFacadeService } from "../../../list/services/store/checklist-facade.service";
import { ElementTypeControle } from "../../enums/element-type-controle";
import { ControleHistoryFilters } from "../../models/controle-history.model";
import { ControleNonApplicableApiService } from "../controle-non-applicable-api.service";
import { ReponseNonApplicablePayload } from "../../models/controle-non-applicable.model";

@Injectable({ providedIn: "root" })
export class ControleFacadeService extends EntityFacadeService<
  Controle,
  ControleApiService,
  ControleState,
  EntityMetadata,
  ControleStoreService
> {
  private contexteEssai = inject(ContexteEssaiService);
  checklistFacade = inject(ChecklistFacadeService);
  private naApi = inject(ControleNonApplicableApiService);

  constructor() {
    const api = inject(ControleApiService);
    const store = inject(ControleStoreService);
    super(api, store);
    store.initStatus(ControleFacadeService);
  }

  //todo yoann passer en computed
  getByPodId(podId: string) {
    return this.elements()
      .filter((e) => e()?.podId === podId)
      .map((e) => e());
  }

  controlesGeneraux = computed(() => {
    return this.elements()
      .map((e) => e())
      .filter((e) => {
        return (
          [
            ElementTypeControle.START_OPE_GENERAUX,
            ElementTypeControle.END_OPE_GENERAUX,
            ElementTypeControle.LOT_RECEPTION_GENERAUX,
            ElementTypeControle.PREPARATION_EXPE_GENERAUX,
            ElementTypeControle.DECHARGEMENT_GENERAUX,
            ElementTypeControle.DECHARGEMENT_ARTICLE_GENERAUX,
            ElementTypeControle.EXPEDITION_GENERAUX,
            ElementTypeControle.LIBERATOIRE_GENERAUX,
            ElementTypeControle.CHARGEMENT_GENERAUX,
            ElementTypeControle.CHARGEMENT_ARTICLE_GENERAUX,
            ElementTypeControle.CHARGEMENT_UNITE_GENERAUX,
            ElementTypeControle.PRODUCTION_GENERAUX,
          ].includes(e.elementType) && e.model
        );
      });
  });

  getControlesByType(type: ElementTypeControle, isModel = true) {
    return this.elements()
      .map((e) => e())
      .filter((e) => e.elementType === type && e.model === isModel);
  }

  @UpdateStatus
  async loadForPodId(podId: string) {
    try {
      const controles = await firstValueFrom(this.api.loadForPodId(podId));
      controles.forEach((c) => {
        this.store.upsertOne(c);
      });
      return controles;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @UpdateStatus
  async loadForPodIds(podIds: string[], posteIds: string[] = []) {
    try {
      const controles = await firstValueFrom(
        this.api.loadForPodIds(podIds, posteIds),
      );
      controles.forEach((c) => {
        this.store.upsertOne(c);
      });
      return controles;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @Untrack
  @UpdateStatus
  async duplicate(controle: Controle) {
    const saved = await firstValueFrom(this.api.duplicate(controle));
    this.store.upsertOne(saved);
    return saved;
  }

  @Untrack
  @UpdateStatus
  async answer(controle: Controle) {
    /*
     * ⚠️ **Dans le cockpit d'un essai de développement, l'enregistrement passe par la route de l'essai.**
     *
     * Un contrôle de **marquage** compare la saisie au numéro de lot et à la date limite. Un essai ne
     * produit ni l'un ni l'autre : ils viennent de l'identité de son **projet** de recette, que seule
     * cette route fournit (`recette/essais/:essaiId/controles/answer`). Par la route générique, la
     * comparaison n'a rien à lire et le contrôle ressort « incomplet », sans que personne ne sache
     * pourquoi.
     *
     * ⚠️ **Le contexte est un contexte d'ÉCRAN** (`ContexteEssaiService`), posé par le cockpit et retiré
     * en partant. Le faire descendre en entrée aurait touché trois composants de PRODUCTION pour un
     * besoin de formulation — le contrôle est affiché cinq composants plus bas.
     *
     * ⚠️ **L'OPÉRATION dépliée part avec**, quand il y en a une : c'est elle qui rattache le relevé à la
     * manip où il a été pris. Sans elle, le serveur doit deviner par la fenêtre de temps — juste tant
     * qu'une seule opération tourne, faux dès qu'une cuisson est relancée pendant qu'une autre est en
     * cours. Le paramètre absent n'efface jamais un rattachement déjà écrit.
     *
     * ⚠️ **Le reste ne change pas** : le store est mis à jour de la même façon, avec le contrôle rendu par
     * le serveur — c'est lui qui porte l'état recalculé (`CONFORME` / `INCOMPLET` / `NOT_CONFORME`).
     */
    const essaiId = this.contexteEssai.essaiId();
    const saved = await firstValueFrom(
      essaiId
        ? this.api.answerForEssai(controle, essaiId, this.contexteEssai.operationEssaiId())
        : this.api.answer(controle),
    );
    this.store.upsertOne(saved);
    return saved;
  }

  @Untrack
  @UpdateStatus
  async answerForChecklist(controle: Controle, checklistId: string) {
    const saved = await firstValueFrom(
      this.api.answerForChecklist(controle, checklistId),
    );
    this.store.upsertOne(saved.controle);
    this.checklistFacade.store.upsertOne(saved.checklist);
    return saved;
  }

  /**
   * Réponse « Non applicable » (06/10/2026, gestes 1 et 2) : le serveur crée TOUJOURS une instance
   * neuve, sans enregistrement (jamais une réponse réécrite) ; elle entre dans le store comme une
   * réponse. Les refus (409 déjà répondu, 403 manquée hors admin, 409 étape close) sont dits par
   * l'intercepteur et remontent à l'appelant.
   */
  @Untrack
  @UpdateStatus
  async declarerNonApplicable(body: ReponseNonApplicablePayload) {
    const saved = await firstValueFrom(this.naApi.declarer(body));
    if (saved) this.store.upsertOne(saved);
    return saved;
  }

  /** Retire une réponse « Non applicable » : la case redevient à faire (ou manquée). */
  @Untrack
  @UpdateStatus
  async annulerNonApplicable(id: string) {
    const res = await firstValueFrom(this.naApi.annuler(id));
    this.store.removeOne(res?.id ?? id);
    return res;
  }

  /**
   * Historique des réponses d'un contrôle définition — retour DIRECT, sans
   * passer par le store : les réponses portent un podId d'exécution, les
   * upserter polluerait `getByPodId()` des écrans de production.
   */
  @Untrack
  @UpdateStatus
  async loadHistory(controleId: string, filters?: ControleHistoryFilters) {
    return await firstValueFrom(this.api.loadHistory(controleId, filters));
  }

  /** Articles concernés par un contrôle (#2) : retour direct, jamais le store. */
  @Untrack
  @UpdateStatus
  async loadArticlesConcernes(controleId: string) {
    return await firstValueFrom(this.api.loadArticlesConcernes(controleId));
  }

  /** Portée d'un ciblage non enregistré (retour direct, pas le store). */
  loadArticlesPourCiblage(ciblage: CiblageArticles) {
    return this.api.loadArticlesPourCiblage(ciblage);
  }

  /** Contrôles effectifs d'un article (#2) : retour direct, jamais le store (ce sont des vues, pas des entités). */
  @Untrack
  @UpdateStatus
  async loadControlesEffectifs(articleId: string) {
    return await firstValueFrom(this.api.loadControlesEffectifs(articleId));
  }

  /** Version export (plafond 5000) — même retour direct, jamais le store. */
  @Untrack
  @UpdateStatus
  async loadHistoryExport(controleId: string, filters?: ControleHistoryFilters) {
    return await firstValueFrom(this.api.loadHistoryExport(controleId, filters));
  }

  @Untrack
  @UpdateStatus
  async loadControlesGeneraux(elemTypeControle?: ElementTypeControle) {
    const controles = await firstValueFrom(
      this.api.loadControlesGeneraux(elemTypeControle),
    );
    this.store.addMany(controles);
  }

  @Untrack
  @UpdateStatus
  async upsertAll(payload: { controles: Controle[] }) {
    const saved = await firstValueFrom(this.api.saveAll(payload));
    saved.forEach((saved: Controle) => {
      this.store.upsertOne(saved);
    });
    return saved;
  }

  @Untrack
  @UpdateStatus
  async createPod() {
    return await firstValueFrom(this.api.createPod());
  }
}
